"use client";
import { useRef, useEffect } from "react";
import { Renderer, Program, Triangle, Mesh } from "ogl";
import { useIsMobile } from "@/utils/useIsMobile";

interface LightRaysProps {
  raysColor?: string;
  raysSpeed?: number;
  lightSpread?: number;
  rayLength?: number;
  fadeDistance?: number;
  saturation?: number;
  noiseAmount?: number;
  distortion?: number;
  className?: string;
}

// Rays are soft gradients, so rendering below screen resolution and letting the
// browser upscale looks the same while shading far fewer pixels (0.5 = 1/4 of the pixels)
const RENDER_SCALE = 0.5;
// Slow-moving rays don't need 60fps; 30fps halves GPU work
const FRAME_INTERVAL = 1000 / 30;
const MOBILE_BREAKPOINT = 1025;

const hexToRgb = (hex: string): [number, number, number] => {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return m ? [parseInt(m[1], 16) / 255, parseInt(m[2], 16) / 255, parseInt(m[3], 16) / 255] : [1, 1, 1];
};

const getAnchorAndDir = (w: number, h: number): { anchor: [number, number]; dir: [number, number] } => {
  const outside = 0.2;
  return { anchor: [0.5 * w, -outside * h], dir: [0, 1] };
};

type Vec2 = [number, number];
type Vec3 = [number, number, number];

interface Uniforms {
  iTime: { value: number };
  iResolution: { value: Vec2 };
  rayPos: { value: Vec2 };
  rayDir: { value: Vec2 };
  raysColor: { value: Vec3 };
  raysSpeed: { value: number };
  lightSpread: { value: number };
  rayLength: { value: number };
  pulsating: { value: number };
  fadeDistance: { value: number };
  saturation: { value: number };
  noiseAmount: { value: number };
  distortion: { value: number };
}

const vert = `
  attribute vec2 position;
  varying vec2 vUv;
  void main() {
  vUv = position * 0.5 + 0.5;
  gl_Position = vec4(position, 0.0, 1.0);
  }`;

const frag = `
  precision highp float;

  uniform float iTime;
  uniform vec2  iResolution;

  uniform vec2  rayPos;
  uniform vec2  rayDir;
  uniform vec3  raysColor;
  uniform float raysSpeed;
  uniform float lightSpread;
  uniform float rayLength;
  uniform float pulsating;
  uniform float fadeDistance;
  uniform float saturation;
  uniform float noiseAmount;
  uniform float distortion;

  varying vec2 vUv;

  float noise(vec2 st) {
  return fract(sin(dot(st.xy, vec2(12.9898,78.233))) * 43758.5453123);
  }

  float rayStrength(vec2 raySource, vec2 rayRefDirection, vec2 coord,
                  float seedA, float seedB, float speed) {
  vec2 sourceToCoord = coord - raySource;
  vec2 dirNorm = normalize(sourceToCoord);
  float cosAngle = dot(dirNorm, rayRefDirection);

  float distortedAngle = cosAngle + distortion * sin(iTime * 2.0 + length(sourceToCoord) * 0.01) * 0.2;

  float spreadFactor = pow(max(distortedAngle, 0.0), 1.0 / max(lightSpread, 0.001));

  float distance = length(sourceToCoord);
  float maxDistance = iResolution.x * rayLength;
  float lengthFalloff = clamp((maxDistance - distance) / maxDistance, 0.0, 1.0);

  float fadeFalloff = clamp((iResolution.x * fadeDistance - distance) / (iResolution.x * fadeDistance), 0.5, 1.0);
  float pulse = pulsating > 0.5 ? (0.8 + 0.2 * sin(iTime * speed * 3.0)) : 1.0;

  float baseStrength = clamp(
      (0.45 + 0.15 * sin(distortedAngle * seedA + iTime * speed)) +
      (0.3 + 0.2 * cos(-distortedAngle * seedB + iTime * speed)),
      0.0, 1.0
  );

  return baseStrength * lengthFalloff * fadeFalloff * spreadFactor * pulse;
  }

  void mainImage(out vec4 fragColor, in vec2 fragCoord) {
  vec2 coord = vec2(fragCoord.x, iResolution.y - fragCoord.y);

  vec4 rays1 = vec4(1.0) *
              rayStrength(rayPos, rayDir, coord, 36.2214, 21.11349,
                          1.5 * raysSpeed);
  vec4 rays2 = vec4(1.0) *
              rayStrength(rayPos, rayDir, coord, 22.3991, 18.0234,
                          1.1 * raysSpeed);

  fragColor = rays1 * 0.5 + rays2 * 0.4;

  if (noiseAmount > 0.0) {
      float n = noise(coord * 0.01 + iTime * 0.1);
      fragColor.rgb *= (1.0 - noiseAmount + noiseAmount * n);
  }

  float brightness = 1.0 - (coord.y / iResolution.y);
  fragColor.x *= 0.1 + brightness * 0.8;
  fragColor.y *= 0.3 + brightness * 0.6;
  fragColor.z *= 0.5 + brightness * 0.5;

  if (saturation != 1.0) {
      float gray = dot(fragColor.rgb, vec3(0.299, 0.587, 0.114));
      fragColor.rgb = mix(vec3(gray), fragColor.rgb, saturation);
  }

  fragColor.rgb *= raysColor;
  }

  void main() {
  vec4 color;
  mainImage(color, gl_FragCoord.xy);
  gl_FragColor  = color;
  }`;

const LightRays: React.FC<LightRaysProps> = ({ raysColor = "#ffffff", raysSpeed = 0.9, lightSpread = 1.0, rayLength = 2, fadeDistance = 1.6, saturation = 1.3, noiseAmount = 0.0, distortion = 0.0 }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const uniformsRef = useRef<Uniforms | null>(null);

  // Latest props, read once when WebGL is created (later changes go through the uniforms effect)
  const propsRef = useRef({ raysColor, raysSpeed, lightSpread, rayLength, fadeDistance, saturation, noiseAmount, distortion });
  propsRef.current = { raysColor, raysSpeed, lightSpread, rayLength, fadeDistance, saturation, noiseAmount, distortion };

  const isMob = useIsMobile(MOBILE_BREAKPOINT);

  // WebGL is created once (lazily, on first visibility) and only the render loop is
  // paused/resumed when scrolling, instead of rebuilding the context every time
  useEffect(() => {
    const container = containerRef.current;
    if (!container || isMob || window.innerWidth < MOBILE_BREAKPOINT) return;

    let renderer: Renderer | null = null;
    let mesh: Mesh | null = null;
    let rafId = 0;
    let lastFrame = 0;
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    const updatePlacement = () => {
      if (!renderer || !uniformsRef.current) return;

      const { clientWidth: wCSS, clientHeight: hCSS } = container;
      renderer.setSize(wCSS, hCSS);

      const w = wCSS * renderer.dpr;
      const h = hCSS * renderer.dpr;
      const u = uniformsRef.current;
      u.iResolution.value = [w, h];

      const { anchor, dir } = getAnchorAndDir(w, h);
      u.rayPos.value = anchor;
      u.rayDir.value = dir;
    };

    const render = (t: number) => {
      if (!renderer || !mesh || !uniformsRef.current) return;
      uniformsRef.current.iTime.value = t * 0.001;
      renderer.render({ scene: mesh });
    };

    const init = () => {
      const p = propsRef.current;

      renderer = new Renderer({ dpr: RENDER_SCALE, alpha: true });
      const gl = renderer.gl;
      gl.canvas.style.width = "100%";
      gl.canvas.style.height = "100%";
      container.appendChild(gl.canvas);

      const uniforms: Uniforms = {
        iTime: { value: 0 },
        iResolution: { value: [1, 1] },
        rayPos: { value: [0, 0] },
        rayDir: { value: [0, 1] },
        raysColor: { value: hexToRgb(p.raysColor) },
        raysSpeed: { value: p.raysSpeed },
        lightSpread: { value: p.lightSpread },
        rayLength: { value: p.rayLength },
        pulsating: { value: 0.0 },
        fadeDistance: { value: p.fadeDistance },
        saturation: { value: p.saturation },
        noiseAmount: { value: p.noiseAmount },
        distortion: { value: p.distortion },
      };
      uniformsRef.current = uniforms;

      const program = new Program(gl, { vertex: vert, fragment: frag, uniforms });
      mesh = new Mesh(gl, { geometry: new Triangle(gl), program });

      updatePlacement();
      window.addEventListener("resize", updatePlacement);
    };

    const loop = (t: number) => {
      rafId = requestAnimationFrame(loop);
      if (t - lastFrame < FRAME_INTERVAL) return;
      lastFrame = t;
      render(t);
    };

    const start = () => {
      if (!renderer) init();
      if (reducedMotion) {
        render(0); // single static frame
        return;
      }
      if (!rafId) rafId = requestAnimationFrame(loop);
    };

    const stop = () => {
      cancelAnimationFrame(rafId);
      rafId = 0;
    };

    const observer = new IntersectionObserver(([entry]) => (entry.isIntersecting ? start() : stop()));
    observer.observe(container);

    return () => {
      observer.disconnect();
      stop();
      window.removeEventListener("resize", updatePlacement);

      if (renderer) {
        const gl = renderer.gl;
        gl.getExtension("WEBGL_lose_context")?.loseContext();
        gl.canvas.remove();
      }
      uniformsRef.current = null;
    };
  }, [isMob]);

  // Prop changes only update uniforms - no WebGL rebuild
  useEffect(() => {
    const u = uniformsRef.current;
    if (!u) return;

    u.raysColor.value = hexToRgb(raysColor);
    u.raysSpeed.value = raysSpeed;
    u.lightSpread.value = lightSpread;
    u.rayLength.value = rayLength;
    u.fadeDistance.value = fadeDistance;
    u.saturation.value = saturation;
    u.noiseAmount.value = noiseAmount;
    u.distortion.value = distortion;
  }, [raysColor, raysSpeed, lightSpread, rayLength, fadeDistance, saturation, noiseAmount, distortion]);

  if (isMob) return;

  return <div ref={containerRef} className="hidden lg:block w-full h-full pointer-events-none overflow-hidden absolute z-0" />;
};

export default LightRays;
